import { Processor } from '@frontend/api-payments';
import { payoutDescriptorMutations } from '@frontend/api-payouts';
import { Divider } from '@frontend/divider';
import { useTranslation, Trans } from '@frontend/i18n';
import { useStripeDashboardLink } from '@frontend/payments-hooks';
import {
  Chip,
  ExternalLinkIcon,
  Heading,
  Text,
  TextLink,
  TextField,
  useFormField,
  Button,
  useAlert,
} from '@frontend/design-system';
import { ContactPayments } from '../../../molecules/contact-payments';
import StripeLogoImg from '../../assets/stripe.svg?react';
import { merchantStatusStyles, stripeCardStyles } from '../styles';
import { StatusText } from './status-text';

type StripeCardProps = {
  activeProcessor?: Processor;
  merchantName?: string;
  chargesEnabled: boolean;
  payoutDescriptor?: string;
  payoutsEnabled: boolean;
  locationId: string;
};
export const StripeCard = ({
  activeProcessor,
  merchantName,
  chargesEnabled,
  payoutDescriptor,
  payoutsEnabled,
  locationId,
}: StripeCardProps) => {
  const { t } = useTranslation('payments', { keyPrefix: 'settings' });
  const alert = useAlert();

  const { mutate: updatePayoutDescriptor, isLoading: isUpdating } = payoutDescriptorMutations.useUpdatePayoutDescriptor(
    {
      locationId,
      onSuccess: () => alert.success(t('Payout descriptor updated successfully')),
      onError: () => alert.error(t('Failed to update payout descriptor')),
    }
  );

  const { handleOpenStripeDashboard, isGettingDashboardLink } = useStripeDashboardLink({ locationId });

  const payoutDescriptFormField = useFormField({
    type: 'text',
    validateOnChange: true,
    value: payoutDescriptor || undefined,
    validator: ({ value }) => {
      if (value && value.length > 22) {
        return t('Must be 22 characters or less');
      }
      if (value && value.length < 5) {
        return t('Must be at least 5 characters');
      }
      return '';
    },
  });

  const onSubmit = () => {
    updatePayoutDescriptor({ descriptor: payoutDescriptFormField.value });
  };

  return (
    <>
      <div css={merchantStatusStyles.title}>
        {activeProcessor && (
          <div css={stripeCardStyles.stripeLogo}>
            <StripeLogoImg />
            <Chip variant='success'>{t('Active')}</Chip>
          </div>
        )}
        {activeProcessor &&
          (isGettingDashboardLink ? (
            <Text color='disabled'>{t('Opening Stripe Dashboard...')}</Text>
          ) : (
            <TextLink
              size='large'
              onClick={handleOpenStripeDashboard}
              data-trackingid='pay-portal-settings-link-stripedash'
              css={stripeCardStyles.dashboardLink}
            >
              {t('Stripe Dashboard')}
              <ExternalLinkIcon color='primary' size={16} css={stripeCardStyles.dashboardLinkIcon} />
            </TextLink>
          ))}
      </div>
      <Text color='light' css={{ margin: 0 }}>
        {t('Merchant Account')}
      </Text>
      <Heading as='p' level={3} css={stripeCardStyles.heading}>
        {merchantName}
      </Heading>
      <div css={merchantStatusStyles.accountInfoContainer}>
        <StatusText label={t('Payment Status')} status={chargesEnabled} trackingId='paymentstatus' />
        <StatusText label={t('Payout Status')} status={payoutsEnabled} trackingId='payoutstatus' />
      </div>
      <ContactPayments locationId={locationId} />
      <div css={merchantStatusStyles.accountInfoContainer}>
        <div css={merchantStatusStyles.accountInfo}>
          <Text size='medium' color='light'>
            {t('Account ID')}
          </Text>
          <Text weight='bold' as='span'>
            {activeProcessor?.stripeId}
          </Text>
        </div>
        <div css={merchantStatusStyles.accountInfo}>
          <Text size='medium' color='light'>
            {t('Account Type')}
          </Text>
          <Text weight='bold' as='span'>
            {activeProcessor?.stripeAccountType}
          </Text>
        </div>
      </div>
      <Divider />
      <div css={stripeCardStyles.payoutDescriptorContainer}>
        <div css={stripeCardStyles.payoutDescriptorField}>
          <TextField name='payout-descriptor' label={t('Payout Descriptor')} {...payoutDescriptFormField} />
          <Trans>
            <Text size='medium' css={stripeCardStyles.payoutDescriptorHelperText} color='light'>
              Customize your payout descriptor to help identify and reconcile bank deposits. Must be 5-22 alphanumeric
              characters.
            </Text>
          </Trans>
        </div>
        <Button
          variant='primary'
          type='submit'
          loading={isUpdating}
          disabled={
            !payoutDescriptFormField.value ||
            !!payoutDescriptFormField.error ||
            payoutDescriptor === payoutDescriptFormField.value
          }
          css={stripeCardStyles.payoutDescriptorButton}
          onClick={onSubmit}
        >
          {t('Save Changes')}
        </Button>
      </div>
    </>
  );
};
